import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  describeChanges,
  LineEdits,
  LineKind,
  postRecalculate,
  recalculationError,
  recalculationProblems,
  rowKey,
  RowProblem,
  saveBlockedBy,
  useSaveLineSettings,
} from '../lineRecalculation'
import { toastLineSaved, toastLineSaveFailed } from '../LinePreviewNote'
import { apiEdit, TOTAL_SHAPE, TotalRow } from './totalRows'

type Row = Record<string, unknown>

/** A planner's override edits on one row, under the grid's names. */
export type OverrideEdit = {
  minOverride?: number | null
  maxOverride?: number | null
}

const DERIVED_FIELDS = [
  'salesRate',
  'naturalCycle',
  'initialCycle',
  'batchMin',
  'minCycle',
  'maxCycle',
  'cycleSetBy',
  'replenishmentTypeCode',
  'isIncluded',
  'isOverriden',
]

/**
 * Pending cycle-override edits for the Total production cycle view, which
 * draws its own rows rather than a grid. The same flow as the grids
 * (lineRecalculation.ts): every change is previewed as a dry run through the
 * API's cycle rules, the returned rows overlay the saved ones (batch-mates
 * and SKU groups included), refused edits come back as problems, and Save
 * sends the same edits.
 *
 * `rows` must be every row the page has (not a filtered view), so changing a
 * filter doesn't drop the edits.
 */
export const useCycleEdits = ({
  projectId,
  scenarioId,
  rows,
}: {
  projectId: string
  scenarioId: string
  rows: TotalRow[]
}) => {
  const [pending, setPending] = useState<Map<string, OverrideEdit>>(new Map())
  const [overlay, setOverlay] = useState<Map<string, Row>>(new Map())
  const [changed, setChanged] = useState<Map<string, Set<string>>>(new Map())
  const [problems, setProblems] = useState<RowProblem[]>([])
  const [summary, setSummary] = useState<string | null>(null)
  const [blockedBy, setBlockedBy] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const latest = useRef(0)

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])
  const idByKey = useMemo(
    () =>
      new Map(rows.map((r) => [`${r.rowKind}|${rowKey(r.rowKind, r)}`, r.id])),
    [rows]
  )
  const idOf = useCallback(
    (kind: LineKind, row: Row) => idByKey.get(`${kind}|${rowKey(kind, row)}`),
    [idByKey]
  )

  const reset = useCallback(() => {
    latest.current += 1
    setPending(new Map())
    setOverlay(new Map())
    setChanged(new Map())
    setProblems([])
    setSummary(null)
    setBlockedBy(null)
    setIsPreviewing(false)
  }, [])

  // Fresh rows from the server (a save, a refetch): the edits are done.
  useEffect(reset, [rows, reset])

  const edits = useMemo((): LineEdits => {
    const out: LineEdits = { products: [], batches: [] }
    pending.forEach((edit, id) => {
      const row = byId.get(id)
      if (!row) return
      out[row.rowKind]?.push(apiEdit(row, edit))
    })
    return out
  }, [byId, pending])

  // Preview every change, debounced; the newest answer wins.
  useEffect(() => {
    if (!pending.size) {
      latest.current += 1
      setOverlay(new Map())
      setChanged(new Map())
      setProblems([])
      setSummary(null)
      setBlockedBy(null)
      setIsPreviewing(false)
      return
    }
    const request = ++latest.current
    setIsPreviewing(true)
    const timer = setTimeout(async () => {
      try {
        const result = await postRecalculate({
          projectId,
          scenarioId,
          edits,
          dryRun: true,
        })
        if (request !== latest.current) return
        const nextOverlay = new Map<string, Row>()
        TOTAL_SHAPE.kinds.forEach((kind) =>
          (result[kind] ?? []).forEach((apiRow) => {
            const id = idOf(kind, apiRow)
            if (!id) return
            const values: Row = {}
            Object.entries(apiRow).forEach(([field, value]) => {
              const name = TOTAL_SHAPE.fieldOf(kind, field)
              if (DERIVED_FIELDS.includes(name)) values[name] = value
            })
            nextOverlay.set(id, values)
          })
        )
        const nextChanged = new Map<string, Set<string>>()
        TOTAL_SHAPE.kinds.forEach((kind) =>
          result.changes[kind].forEach((change) => {
            const id = idOf(kind, change)
            if (!id) return
            nextChanged.set(
              id,
              new Set(
                Object.keys(change.fields).map((f) =>
                  TOTAL_SHAPE.fieldOf(kind, f)
                )
              )
            )
          })
        )
        setOverlay(nextOverlay)
        setChanged(nextChanged)
        setProblems(result.problems ?? [])
        setSummary(`Preview: ${describeChanges(result)}`)
        setBlockedBy(saveBlockedBy(result))
      } catch (error) {
        if (request !== latest.current) return
        setSummary(`Preview unavailable: ${recalculationError(error)}`)
        setProblems(recalculationProblems(error))
        setBlockedBy('The preview failed')
      } finally {
        if (request === latest.current) setIsPreviewing(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [edits, idOf, pending.size, projectId, scenarioId])

  /** Set (or with null, clear) one override; back to the saved value drops the edit. */
  const setOverride = useCallback(
    (id: string, values: OverrideEdit) => {
      const saved = byId.get(id)
      if (!saved) return
      setPending((before) => {
        const next = new Map(before)
        const edit: OverrideEdit = { ...next.get(id), ...values }
        ;(Object.keys(edit) as (keyof OverrideEdit)[]).forEach((field) => {
          if ((edit[field] ?? null) === (saved[field] ?? null))
            delete edit[field]
        })
        if (Object.keys(edit).length) next.set(id, edit)
        else next.delete(id)
        return next
      })
    },
    [byId]
  )

  /** A row as it would be: saved, then the preview's values, then the edits. */
  const view = useCallback(
    (row: TotalRow): TotalRow => ({
      ...row,
      ...overlay.get(row.id),
      ...pending.get(row.id),
    }),
    [overlay, pending]
  )

  const problemsFor = useCallback(
    (id: string) => problems.filter((p) => idOf(p.kind, p) === id),
    [idOf, problems]
  )

  const { mutate: save, isPending: isSaving } = useSaveLineSettings(
    projectId,
    scenarioId,
    toastLineSaved,
    (error) => {
      setProblems(recalculationProblems(error))
      toastLineSaveFailed(error)
    }
  )

  return {
    pendingCount: pending.size,
    isEdited: (id: string) => pending.has(id),
    changedFields: (id: string) => changed.get(id),
    setOverride,
    view,
    problems,
    problemsFor,
    summary,
    isPreviewing,
    saveBlockedReason: blockedBy,
    isSaving,
    save: () => save(edits),
    discard: reset,
  }
}
